import { useEffect, useRef, useState } from "react";
import {
  IonItem,
  IonProgressBar,
  IonLabel,
  IonList,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
} from "@ionic/react";
import { isArray } from "lodash";
import { FileUploader } from "react-drag-drop-files";
import { attachOutline } from "ionicons/icons";

// import { useTranslation } from "react-i18next";
import { usePymbleApi } from "../../hooks/usePymbleApi";

import "./PymbleFileUploader.css";

// const FILE_STORAGE_END_POINT = `${api.myPymble}/filestorage`;
const FILE_STORAGE_END_POINT = "/storage/file/";
const fileTypes = [
  "JPG",
  "PNG",
  "GIF",
  "MOV",
  "MP4",
  "PDF",
  "TXT",
  "DOC",
  "DOCX",
  "ZIP",
  "XLS",
  "XLSX",
  "CSV",
  "PPTX",
];

// FileMetadata:
//    File metadata.
// Structure of the file metadata returned by the API and what
// is stored in the database for each file.
export type FileMetadata = {
  Name: string; // Name of the file (original filename of uploaded file)
  InternalName?: string; // Internal name of the file (ie. the name of the file in Azure Storage)
  Container?: string; // Azure Storage Container name (with optional subfolder)
  Type?: string; // Mime type of the file
  Data?: any; // File data - optional additional data
  Url: string; // URL to the file in Azure Storage
  Size?: number; // Size of the file in bytes
  ArchiveAfter?: string; // Archive the file after this date
  DisposeAfter?: string; // Dispose of the file after this date
  Secure?: boolean; // Secure: true if the file is secured (ie. not accessible to the public and needs an SAS token to access it)
  Private?: boolean; // Private: true if the file is private (ie. not shared with other users)
  Tags?: string[]; // Tags for searching this file
  Token?: string; // Optional - Used for private files.  This is created by the API that returns the File metadata and is used to retrieve the file.
};

type ProgressInfo = {
  percentage: number;
  fileName: string;
};
type ProgressInfos = {
  val: ProgressInfo[];
};

// FileUploadSettings:
//   Settings for file upload.
export type FileUploadSettings = {
  // Azure Storage Container name (with optional subfolder)
  Container: string;
  // Collection name associated with the file
  Collection: string;
  // Archive the file after this many days
  ArchiveAfter?: number;
  // Dispose of the file after this many days
  DisposeAfter?: number;
  // Time to live in seconds for client side cache when retrieving the file
  TTL?: number;
  // Secure the file with SAS token
  Secure?: boolean;
  // Private: true if the file is private (ie. not shared with other users)
  Private?: boolean;
  // Tags
  Tags?: string[];
};

// Default settings for file upload
const settingDefaults = {
  ArchiveAfter: 2, // Archive the file after this many years
  DisposeAfter: 5, // Dispose of the file after this many years
  TTL: 3600, // Time to live in seconds for client side cache when retrieving the file
  Secure: true,
  Tags: [],
};

interface PymbleFileUploaderProps {
  id: string;
  settings: FileUploadSettings;
  showMessages?: boolean;
  onUploadComplete?: (files: FileMetadata[]) => void;
}
/**
 * PymbleFileUploader
 *
 * A file uploader component that uses the Pymble API to upload files to Azure Storage.
 *
 * Drag and drop files onto the drop zone or click the button to select files.
 *
 * - Files are uploaded to Azure Storage and the file metadata is stored in the database.
 * - A progress bar is displayed for each file being uploaded.
 * - Once complete, the file metadata (FileMetadata) is returned to the caller via
 *   the onUploadComplete callback.
 * - Settings for the file upload are passed in via the settings prop, and control where
 *   the files are stored (ie. Azure storage container) and the Atlas DB Collection associated
 *   with the file.  See FileUploadSettings.
 * - ArchiveAfter and DisposeAfter are the number of days after which the file will be archived
 *   then disposed.  These can be set as defaults or overriden on return of API.  See FileUploadSettings.
 * - Secure is a boolean that determines whether the file is stored securely (ie. with a SAS token).
 *   See FileUploadSettings.
 * - Tags is an array of strings that can be used to tag the file.  See FileUploadSettings.
 * - showMessages is a boolean that determines whether messages are displayed to the user.
 */
const PymbleFileUploader: React.FC<PymbleFileUploaderProps> = ({
  settings,
  id,
  showMessages = false,
  onUploadComplete,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [progressInfos, setProgressInfos] = useState<ProgressInfos>({
    val: [],
  });
  const [message, setMessage] = useState<string[]>([]);
  const [dropZoneActive, setDropZoneActive] = useState<boolean>(false);
  const progressInfosRef = useRef<any>({ val: [] });

  // const { t } = useTranslation();
  // Hack to get around the useTranslation hook not working in this component
  const t: any = (key: any, text: any, _options: any) => {
    return text;
  }

  const fileSettings: FileUploadSettings = { ...settingDefaults, ...settings };

  const { callApiS2: callApi } = usePymbleApi();

  const handleFileChange = (file: FileList) => {
    let files = Array.from(file);
    resetProgressInfos();
    resetMessages();
    setSelectedFiles(files);
  };

  const resetProgressInfos = () => {
    setProgressInfos({ val: [] });
  };
  const resetMessages = () => {
    setMessage([]);
  };
  const resetFiles = () => {
    setSelectedFiles([]);
  };
  const resetUpload = () => {
    resetFiles();
    resetProgressInfos();
    resetMessages();
  };

  useEffect(() => {
    resetUpload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Upload files
  // Uses the FileStorage API to upload files to Azure Storage.
  // The API returns the file metadata which is stored in the database.
  // A progress bar is displayed for each file via an array of ProgressInfo objects.
  const upload = async (idx: number, file: File) => {
    if (!progressInfosRef?.current) {
      console.warn("PymbleFileUploader: upload: progressInfosRef is null");
    }
    // eslint-disable-next-line no-unsafe-optional-chaining
    let _progressInfos = [...progressInfosRef?.current?.val];

    let formData = new FormData();

    formData.append("file", file);

    try {
      if (id && settings.Collection) {
        const uploadResult = await callApi(
          "POST",
          FILE_STORAGE_END_POINT +
          "?container=" +
          fileSettings.Container +
          "&collection=" +
          fileSettings.Collection +
          (fileSettings.ArchiveAfter
            ? "&archiveafter=" + fileSettings.ArchiveAfter
            : "") +
          (fileSettings.DisposeAfter
            ? "&disposeafter=" + fileSettings.DisposeAfter
            : "") +
          (fileSettings.TTL ? "&ttl=" + fileSettings.TTL : "") +
          (fileSettings?.Secure ? "&secure=" + fileSettings.Secure : "") +
          (fileSettings?.Private ? "&private=" + fileSettings.Private : "") +
          (isArray(fileSettings?.Tags)
            ? "&tags=" + fileSettings.Tags.join(",")
            : "") +
          "&id=" +
          id,
          {
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (event: any) => {
              const progress = Math.round((100 * event.loaded) / event.total);
              _progressInfos[idx].percentage = progress;
              setProgressInfos({ val: _progressInfos });
            },
          }
        );

        setMessage([
          ...message,
          "Uploaded the file successfully: " + file.name,
        ]);

        return uploadResult;
      } else {
        console.warn("PymbleFileUploader: upload: id or collection is not set");
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      _progressInfos[idx].percentage = 0;
      setProgressInfos({ val: _progressInfos });
      setMessage([...message, "Could not upload the file: " + file.name]);
    }
    return null;
  };

  // Upload files triggered by the upload button
  // Uploads the files in the selectedFiles array.
  // The upload function returns the file metadata which is stored in the database.
  const uploadFiles = async () => {
    const files = Array.from(selectedFiles);

    let __progressInfos: ProgressInfo[] = files.map((file: File) => ({
      percentage: 0,
      fileName: file?.name,
    }));

    if (progressInfosRef?.current) {
      progressInfosRef.current.val = __progressInfos;
    } else {
      console.warn(
        "PymbleFileUploader 2: uploadFiles: progressInfosRef is null"
      );
    }

    const uploadPromises = files.map((file, i) => upload(i, file));

    const results = await Promise.all(uploadPromises);

    let fileMetaResults: FileMetadata[] = [];
    for (let i = 0; i < results.length; i++) {
      fileMetaResults[i] = results[i]?.File;
    }

    // Call onUploadComplete
    console.log("File Upload Results:", fileMetaResults);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onUploadComplete && onUploadComplete(fileMetaResults);

    return fileMetaResults;
  };

  useEffect(() => {
    if (selectedFiles.length > 0) {
      uploadFiles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles]);

  return (
    <div
      className={
        "pymble-file-uploader" +
        (dropZoneActive ? " file-drop-zone-active" : "")
      }
    >
      <IonGrid >
        <IonRow>
          {progressInfos && progressInfos.val.length > 0 && (
            <IonCol>
              {progressInfos.val.map((pInfo, index) => (
                <IonItem key={"prog-" + index}>
                  <IonLabel slot="start">{pInfo.fileName}</IonLabel>
                  <IonProgressBar
                    buffer={0.06}
                    value={pInfo.percentage / 100}
                  ></IonProgressBar>
                </IonItem>
              ))}
            </IonCol>
          )}
          <IonCol>
            <FileUploader
              handleChange={handleFileChange}
              name="file"
              types={fileTypes}
              multiple={true}
              className="file-uploader-target"
              onDraggingStateChange={(dragging: boolean) => {
                setDropZoneActive(dragging);
              }}
              hoverTitle={" "}
            >
              <div className="uploader-div">
                <IonButton
                  fill="outline"
                  size="small"
                  className="ion-text-capitalize file-uploader-button"
                >
                  <IonIcon icon={attachOutline} slot="start" />
                  {t(
                    "upload.file-upload-drop-here",
                    "Attach files",
                    {
                      ns: "common",
                    }
                  )}
                </IonButton>
                {/* <div className="uploader-msg">
                  {t(
                    "upload.file-upload-message",
                    "Attach as many files as you would like, each file should not exceed 5mb",
                    {
                      ns: "common",
                    }
                  )}
                </div> */}
              </div>
            </FileUploader>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            {showMessages && message.length > 0 && (
              <IonList className="file-uploader-messages">
                {message.map((item, i) => {
                  return (
                    <IonItem>
                      <li key={i}>{item}</li>
                    </IonItem>
                  );
                })}
              </IonList>
            )}
          </IonCol>
        </IonRow>
        {(message.length > 0 || progressInfos?.val?.length > 0) && (
          <IonRow>
            <IonCol>
              <IonItem lines="none">
                <IonButton
                  slot="end"
                  fill="clear"
                  size="small"
                  onClick={resetUpload}
                >
                  {t("upload.file-upload-clear-status", "Clear Upload Status", {
                    ns: "common",
                  })}
                </IonButton>
              </IonItem>
            </IonCol>
          </IonRow>
        )}
      </IonGrid>
    </div>
  );
};

export default PymbleFileUploader;
